import React, { useEffect, useState } from 'react';

import { isRecord, unwrapList, unwrapRecord } from './api-client';
import type { AdminApiClient, JsonRecord } from './api-client';
import { pathFor } from './page-registry';
import './operations-workspace.css';

type Params = Record<string, string>;
type LoadState<T = unknown> = { data?: T; error?: unknown; loading: boolean; reload: () => void };

interface PageProps {
  api: AdminApiClient;
  targetAppId: string;
  pageId: string;
  params: Params;
}

const statusLabels: Record<string, string> = {
  ACTIVE: '正常',
  APPROVED: '已审核',
  CONFIRMED: '已确认',
  DISABLED: '已停用',
  DRAFT: '草稿',
  ENABLED: '已启用',
  ESTIMATED: '预估',
  EXPIRED: '已失效',
  PAUSED: '暂停',
  PENDING: '待处理',
  REVIEW_PENDING: '待审核',
  SETTLED: '已结算',
  SETTLEMENT_PENDING: '待结算',
};

const businessLabels: Record<string, string> = {
  FULL_REDUCTION: '满减券',
  FIXED_CASH: '固定现金券',
  DISCOUNT: '折扣券',
  FIXED: '固定金额',
  PERCENTAGE: '销售额百分比',
  SKU: '指定服务套餐',
  PRODUCT: '指定产品',
  CHANNEL: '指定渠道',
  APP: '当前小程序全部业务',
  HOTEL: '酒店 / 业态',
  AGENCY: '旅行机构',
  PROMOTER: '推广员',
  REVERSAL: '退款冲销',
  COMMISSION: '佣金计提',
};

function businessName(value: unknown, fallback = '暂未命名') {
  const result = safe(value, fallback)
    .replace(/\s*[（([][^）)\]]*(?:dev[\s/-]*test|fixture|开发测试)[^）)\]]*[）)\]]/giu, '')
    .replace(/\b(?:dev[\s/-]*test|fixture)\b/giu, '')
    .replace(/\s{2,}/gu, ' ')
    .trim();
  return result || fallback;
}

function customerPageLabel(value: unknown) {
  if (typeof value !== 'string') return '暂未配置';
  if (value.startsWith('FE-DETAIL')) return '产品详情页';
  if (value.startsWith('FE-CITY')) return '城市线路页';
  if (value.startsWith('FE-SKU')) return '套餐选择页';
  if (value.startsWith('FE-SEARCH')) return '搜索结果页';
  return '受控小程序页面';
}

function PromotionCodePreview({
  api,
  appId,
  link,
}: {
  api: AdminApiClient;
  appId: string;
  link?: JsonRecord;
}) {
  const [state, setState] = useState<{
    loading: boolean;
    url?: string;
    provider?: string;
    pending?: boolean;
    error?: string;
  }>({ loading: Boolean(link) });
  const linkId = safe(link?.id, '');
  useEffect(() => {
    if (!linkId) {
      setState({ loading: false });
      return;
    }
    let active = true;
    let objectUrl = '';
    setState({ loading: true });
    void api
      .downloadWithMetadata(
        `/api/admin/m6/apps/${encodeURIComponent(appId)}/promotion-links/${encodeURIComponent(linkId)}/code`,
      )
      .then(
        ({ blob, headers }) => {
          if (!active) return;
          const pending = headers.get('x-external-validation-pending') === 'true';
          if (!pending) {
            if (typeof URL.createObjectURL !== 'function') {
              setState({ loading: false, error: '当前浏览器不支持小程序码预览' });
              return;
            }
            objectUrl = URL.createObjectURL(blob);
          }
          setState({
            loading: false,
            url: objectUrl,
            provider: headers.get('x-mini-program-code-provider') ?? 'UNKNOWN',
            pending,
          });
        },
        (error: unknown) => {
          if (active)
            setState({
              loading: false,
              error: error instanceof Error ? error.message : '小程序码暂不可用',
            });
        },
      );
    return () => {
      active = false;
      if (objectUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(objectUrl);
    };
  }, [api, appId, linkId]);
  if (!link)
    return (
      <div className="r4-state">
        <strong>暂无推广链接</strong>
        <span>请先通过受控接口创建有效推广链接。</span>
      </div>
    );
  if (state.loading)
    return (
      <div className="r5-qr-pending">
        <div>加载中</div>
        <span>正在通过受控接口生成小程序码</span>
      </div>
    );
  if (state.error)
    return (
      <div className="r5-qr-pending">
        <div>未就绪</div>
        <strong>{state.error}</strong>
        <span>请核对当前小程序凭据与运行配置。</span>
      </div>
    );
  if (state.pending)
    return (
      <div className="r5-qr-pending">
        <div>TEST</div>
        <strong>{safe(link.code, businessRef(link.id, '场景码 '))}</strong>
        <span>当前仅作为测试码/试用路径验证证据；生产可投放小程序码仍需微信正式路径校验。</span>
      </div>
    );
  return (
    <div className="r5-qr-pending">
      {state.url && <img className="r5-qr-image" src={state.url} alt="微信小程序推广码" />}
      <strong>{safe(link.code, businessRef(link.id, '场景码 '))}</strong>
      <span>由微信官方接口生成 · {state.provider}</span>
    </div>
  );
}

function useTask<T>(key: string, loader: () => Promise<T>, dependency: unknown): LoadState<T> {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<Omit<LoadState<T>, 'reload'>>({ loading: true });
  useEffect(() => {
    let active = true;
    setState({ loading: true });
    void loader().then(
      (data) => active && setState({ loading: false, data }),
      (error: unknown) => active && setState({ loading: false, error }),
    );
    return () => {
      active = false;
    };
  }, [key, revision, dependency]);
  return { ...state, reload: () => setRevision((value) => value + 1) };
}

function useApi(api: AdminApiClient, path?: string) {
  return useTask(
    path ?? 'round05-disabled',
    () => (path ? api.get(path) : Promise.resolve({})),
    api,
  );
}

function rows(value: unknown): JsonRecord[] {
  return unwrapList(value);
}

function safe(value: unknown, fallback = '暂未记录') {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return fallback;
}

function label(value: unknown, fallback = '待确认') {
  const raw = safe(value, '');
  return statusLabels[raw] ?? businessLabels[raw] ?? fallback;
}

function money(value: unknown) {
  const cents = Number(value);
  return Number.isFinite(cents)
    ? `¥${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '暂未记录';
}

function parseYuanCents(value: string) {
  const match = /^(\d{1,8})(?:\.(\d{1,2}))?$/u.exec(value.trim());
  if (!match) return Number.NaN;
  return Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
}

function percentFromBps(value: unknown) {
  const bps = Number(value);
  return Number.isFinite(bps) ? `${(bps / 100).toFixed(2).replace(/\.00$/u, '')}%` : '暂未记录';
}

function date(value: unknown) {
  if (typeof value !== 'string') return '暂未记录';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '暂未记录';
  return parsed.toLocaleDateString('zh-CN');
}

function businessRef(value: unknown, prefix: string) {
  if (typeof value !== 'string' || !value) return `${prefix}待补充`;
  return `${prefix}${value.replaceAll('-', '').slice(-6).toUpperCase()}`;
}

function commandError(error: unknown) {
  return error instanceof Error && error.message.trim()
    ? error.message
    : '操作未完成，请检查权限和填写内容后重试';
}

function validCode(value: string) {
  return /^[A-Za-z0-9._:-]{1,100}$/u.test(value.trim());
}

function isoDate(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
}

function operationKey(scope: string) {
  return `${scope}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
}

function useCommand() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [failed, setFailed] = useState(false);
  async function run(action: () => Promise<unknown>, success: string, after?: () => void) {
    if (busy) return false;
    setBusy(true);
    setMessage('');
    setFailed(false);
    try {
      await action();
      setMessage(success);
      after?.();
      return true;
    } catch (error) {
      setFailed(true);
      setMessage(commandError(error));
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { busy, failed, message, run, setMessage };
}

function CommandMessage({ command }: { command: ReturnType<typeof useCommand> }) {
  if (!command.message) return null;
  return (
    <div className={`r4-notice ${command.failed ? 'warning' : ''}`} role="status">
      {command.message}
    </div>
  );
}

function tone(value: unknown) {
  const raw = String(value);
  if (['ACTIVE', 'APPROVED', 'CONFIRMED', 'ENABLED', 'SETTLED'].includes(raw)) return 'green';
  if (['PAUSED', 'REVIEW_PENDING', 'SETTLEMENT_PENDING'].includes(raw)) return 'orange';
  if (['DISABLED', 'EXPIRED'].includes(raw)) return 'red';
  return 'gray';
}

function Pill({ value, text }: { value: unknown; text?: string }) {
  return <span className={`canonical-pill ${tone(value)}`}>{text ?? label(value)}</span>;
}

function Header({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="canonical-page-title">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {actions && <div className="canonical-page-actions">{actions}</div>}
    </div>
  );
}

function Panel({
  title,
  hint,
  children,
  className = '',
}: {
  title: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`r4-panel ${className}`}>
      <header>
        <h2>{title}</h2>
        {hint && <span>{hint}</span>}
      </header>
      <div className="r4-panel-body">{children}</div>
    </section>
  );
}

function Metrics({
  items,
}: {
  items: Array<{ label: string; value: React.ReactNode; note?: string }>;
}) {
  return (
    <div className="r4-metrics">
      {items.map((item) => (
        <article key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          {item.note && <small>{item.note}</small>}
        </article>
      ))}
    </div>
  );
}

function State({
  state,
  count,
  noun,
  children,
}: {
  state: LoadState;
  count: number;
  noun: string;
  children: React.ReactNode;
}) {
  if (state.loading) return <div className="r4-state">正在加载{noun}…</div>;
  if (state.error)
    return (
      <div className="r4-state error" role="alert">
        <strong>{noun}读取失败</strong>
        <span>请检查网络或当前账号的数据权限后重试。</span>
        <button onClick={state.reload}>重新加载</button>
      </div>
    );
  if (count === 0) return <div className="r4-state">当前小程序范围暂无{noun}</div>;
  return <>{children}</>;
}

function Filters({
  search,
  setSearch,
  placeholder,
}: {
  search: string;
  setSearch: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="r4-filters">
      <span className="r3-locked-filter">当前授权范围</span>
      <input
        type="search"
        aria-label={placeholder}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={placeholder}
      />
      {search && <button onClick={() => setSearch('')}>清空筛选</button>}
    </div>
  );
}

function navigate(event: React.MouseEvent<HTMLAnchorElement>, href: string) {
  if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function NavLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <a href={href} className={className} onClick={(event) => navigate(event, href)}>
      {children}
    </a>
  );
}

function Notice({ children, warning = false }: { children: React.ReactNode; warning?: boolean }) {
  return <div className={`r4-notice ${warning ? 'warning' : ''}`}>{children}</div>;
}

function EmptyApp() {
  return (
    <div className="r4-state error" role="alert">
      <strong>尚未选择目标小程序</strong>
      <span>请选择小程序后再读取其独立的营销与渠道数据。</span>
    </div>
  );
}

function findName(collection: JsonRecord[], id: unknown, fields: string[]) {
  const item = collection.find((row) => row.id === id);
  if (!item) return '';
  return fields.map((field) => safe(item[field], '')).find(Boolean) ?? '';
}

function CouponPages({
  api,
  appId,
  pageId,
  params,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
  params: Params;
}) {
  const state = useApi(api, `/api/admin/m5/coupon-campaigns?appId=${encodeURIComponent(appId)}`);
  const data = rows(state.data);
  const selected =
    pageId === 'ADM-H02' ? (data.find((row) => row.id === params.id) ?? data[0]) : undefined;
  const [search, setSearch] = useState('');
  const filtered = data.filter((row) =>
    `${businessName(row.name, '')}${safe(row.code, '')}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const active = data.filter((row) => row.status === 'ACTIVE').length;

  if (pageId === 'ADM-H02') {
    return (
      <CouponEditor
        api={api}
        appId={appId}
        state={state}
        {...(selected ? { campaign: selected } : {})}
      />
    );
  }
  return (
    <>
      <Header
        title="优惠券活动"
        subtitle="营销与推广 / 优惠券活动"
        actions={
          <NavLink className="primary" href="#create-coupon">
            + 新建优惠券活动
          </NavLink>
        }
      />
      <Metrics
        items={[
          { label: '活动总数', value: data.length, note: '当前小程序范围' },
          { label: '启用中', value: active, note: '按服务端有效状态' },
          { label: '已暂停', value: data.length - active, note: '可在策略页恢复' },
          {
            label: '规则版本',
            value: data.reduce(
              (sum, row) => sum + (Array.isArray(row.rules) ? row.rules.length : 0),
              0,
            ),
            note: '快照不回写历史订单',
          },
          {
            label: '叠加规则',
            value: data.some(
              (row) =>
                Array.isArray(row.rules) &&
                row.rules.some((rule) => isRecord(rule) && rule.stackable === true),
            )
              ? '存在可叠加规则'
              : '默认不叠加',
            note: '以规则配置为准',
          },
        ]}
      />
      <Panel title="活动列表" hint="金额按元展示，样例数据不作为生产规则">
        <Filters search={search} setSearch={setSearch} placeholder="活动名称 / 策略名称" />
        <State state={state} count={filtered.length} noun="优惠活动">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>活动名称</th>
                  <th>类型</th>
                  <th>面额</th>
                  <th>使用门槛</th>
                  <th>目标人群</th>
                  <th>有效期</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const rule =
                    Array.isArray(row.rules) && isRecord(row.rules[0]) ? row.rules[0] : {};
                  return (
                    <tr key={safe(row.id)}>
                      <td>
                        <b>{businessName(row.name)}</b>
                        <small>{safe(row.code)} · 服务端规则</small>
                      </td>
                      <td>{label(rule.ruleType, '优惠券')}</td>
                      <td>{money(rule.amountCents)}</td>
                      <td>
                        {Number(rule.minSpendCents ?? 0) > 0
                          ? `满 ${money(rule.minSpendCents)} 可用`
                          : '无金额门槛'}
                      </td>
                      <td>按规则 Scope</td>
                      <td>
                        {date(row.validFrom)} 至 {date(row.validTo)}
                      </td>
                      <td>
                        <Pill value={row.status} />
                      </td>
                      <td>
                        <NavLink href={pathFor('ADM-H02', { id: safe(row.id) })}>编辑规则</NavLink>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
      <CouponCreate api={api} appId={appId} state={state} />
      <Notice>一张订单最多使用一张优惠券；是否允许叠加只读取服务端规则，不由页面自行判断。</Notice>
    </>
  );
}

function CouponCreate({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
}) {
  const [name, setName] = useState('');
  const [ruleType, setRuleType] = useState<'FULL_REDUCTION' | 'FIXED_CASH'>('FULL_REDUCTION');
  const [amountYuan, setAmountYuan] = useState('10');
  const [minimumYuan, setMinimumYuan] = useState('100');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function create() {
    const amountCents = parseYuanCents(amountYuan);
    const minSpendCents = ruleType === 'FIXED_CASH' ? 0 : parseYuanCents(minimumYuan);
    if (
      !name.trim() ||
      !Number.isSafeInteger(amountCents) ||
      !Number.isSafeInteger(minSpendCents)
    ) {
      setMessage('请完整填写活动名称与合法金额');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await api.send(`/api/admin/m5/coupon-campaigns?appId=${encodeURIComponent(appId)}`, 'POST', {
        code: `COUPON_${Date.now()}`,
        name: name.trim(),
        validFrom: new Date().toISOString(),
        validTo: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        ruleType,
        amountCents,
        minSpendCents: ruleType === 'FIXED_CASH' ? 0 : minSpendCents,
        validMinutes: 30 * 24 * 60,
        dailyUserLimit: 1,
        orderLimit: 1,
        scopeSnapshot: { scope: 'APP' },
      });
      setName('');
      setMessage('活动与首条规则已创建，历史发券快照不受后续修改影响');
      state.reload();
    } catch {
      setMessage('创建失败，请检查活动编号冲突、金额规则或权限');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Panel title="新建优惠券活动" hint="当前小程序范围" className="r4-command">
      <div id="create-coupon" className="r5-form-grid two">
        <label>
          活动名称
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="例如：支付回访礼"
          />
        </label>
        <label>
          券类型
          <select
            value={ruleType}
            onChange={(event) => setRuleType(event.target.value as 'FULL_REDUCTION' | 'FIXED_CASH')}
          >
            <option value="FULL_REDUCTION">满减券</option>
            <option value="FIXED_CASH">固定现金券</option>
          </select>
        </label>
        <label>
          优惠金额（元）
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amountYuan}
            onChange={(event) => setAmountYuan(event.target.value)}
          />
        </label>
        {ruleType === 'FULL_REDUCTION' && (
          <label>
            最低消费（元）
            <input
              type="number"
              min="0"
              step="0.01"
              value={minimumYuan}
              onChange={(event) => setMinimumYuan(event.target.value)}
            />
          </label>
        )}
      </div>
      <p className="operations-rule-note">
        每单限用一张，不可叠加。
        {ruleType === 'FIXED_CASH' ? '现金券无最低消费门槛。' : '订单满足最低消费金额后可用。'}
      </p>
      <div className="r5-form-actions">
        <button className="primary" disabled={busy} onClick={() => void create()}>
          {busy ? '创建中…' : '创建活动'}
        </button>
        {message && <span>{message}</span>}
      </div>
    </Panel>
  );
}

function CouponEditor({
  api,
  appId,
  state,
  campaign,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState;
  campaign?: JsonRecord;
}) {
  const rule =
    campaign && Array.isArray(campaign.rules) && isRecord(campaign.rules[0])
      ? campaign.rules[0]
      : {};
  const [amountYuan, setAmountYuan] = useState((Number(rule.amountCents ?? 0) / 100).toFixed(2));
  const [minimumYuan, setMinimumYuan] = useState(
    (Number(rule.minSpendCents ?? 0) / 100).toFixed(2),
  );
  const [validMinutes, setValidMinutes] = useState(Number(rule.validMinutes ?? 30));
  const [dailyLimit, setDailyLimit] = useState(Number(rule.dailyUserLimit ?? 1));
  const [orderLimit, setOrderLimit] = useState(Number(rule.orderLimit ?? 1));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    setAmountYuan((Number(rule.amountCents ?? 0) / 100).toFixed(2));
    setMinimumYuan((Number(rule.minSpendCents ?? 0) / 100).toFixed(2));
    setValidMinutes(Number(rule.validMinutes ?? 30));
    setDailyLimit(Number(rule.dailyUserLimit ?? 1));
    setOrderLimit(Number(rule.orderLimit ?? 1));
  }, [rule.id]);
  async function save() {
    if (!campaign || !rule.id || busy) return;
    const amountCents = parseYuanCents(amountYuan);
    const minSpendCents = parseYuanCents(minimumYuan);
    if (!Number.isSafeInteger(amountCents) || !Number.isSafeInteger(minSpendCents)) {
      setMessage('金额必须使用最多两位小数的确定值');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m5/coupon-rules/${encodeURIComponent(safe(rule.id))}?appId=${encodeURIComponent(appId)}`,
        'PATCH',
        {
          ruleType: safe(rule.ruleType),
          amountCents,
          minSpendCents: rule.ruleType === 'FIXED_CASH' ? 0 : minSpendCents,
          validMinutes,
          dailyUserLimit: dailyLimit,
          orderLimit,
          expectedVersion: Number(rule.version),
        },
      );
      setMessage('规则已按版本保存；已发券快照保持不变');
      state.reload();
    } catch {
      setMessage('保存失败，请检查权限或规则版本后重试');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header title="优惠券规则编辑" subtitle="营销与推广 / 优惠券活动 / 规则编辑" />
      <State state={state} count={campaign && rule.id ? 1 : 0} noun="可编辑优惠券规则">
        <Panel
          title={businessName(campaign?.name, '优惠券规则')}
          hint={<Pill value={campaign?.status} />}
        >
          <div className="operations-record-meta">
            <span>券类型：{label(rule.ruleType, '优惠券')}</span>
            <span>
              活动有效期：{date(campaign?.validFrom)} 至 {date(campaign?.validTo)}
            </span>
            <span>{rule.stackable === true ? '允许叠加' : '不可叠加'}</span>
          </div>
          <div className="r5-form-grid">
            <label>
              优惠金额（元）
              <input
                type="number"
                step="0.01"
                value={amountYuan}
                onChange={(event) => setAmountYuan(event.target.value)}
              />
            </label>
            {rule.ruleType !== 'FIXED_CASH' && (
              <label>
                最低消费（元）
                <input
                  type="number"
                  step="0.01"
                  value={minimumYuan}
                  onChange={(event) => setMinimumYuan(event.target.value)}
                />
              </label>
            )}
            <label>
              领取后有效时长（分钟）
              <input
                type="number"
                min="1"
                value={validMinutes}
                onChange={(event) => setValidMinutes(Number(event.target.value))}
              />
            </label>
            <label>
              每用户每日领取上限
              <input
                type="number"
                min="1"
                value={dailyLimit}
                onChange={(event) => setDailyLimit(Number(event.target.value))}
              />
            </label>
            <label>
              每订单使用上限
              <input
                type="number"
                min="1"
                value={orderLimit}
                onChange={(event) => setOrderLimit(Number(event.target.value))}
              />
            </label>
          </div>
          <p className="operations-rule-note">
            修改仅影响后续发券，已发券保持原规则。支付挽回优惠仅在订单仍未支付时发放。
          </p>
          <div className="r5-form-actions">
            <NavLink href={pathFor('ADM-H01')}>取消</NavLink>
            <button className="primary" disabled={busy} onClick={() => void save()}>
              保存规则
            </button>
            {message && <span>{message}</span>}
          </div>
        </Panel>
      </State>
    </>
  );
}

function RescuePages({
  api,
  appId,
  pageId,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
}) {
  const strategies = useApi(
    api,
    pageId === 'ADM-H03'
      ? `/api/admin/m5/rescue-strategies?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const campaigns = useApi(
    api,
    pageId === 'ADM-H03'
      ? `/api/admin/m5/coupon-campaigns?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const funnel = useApi(
    api,
    pageId === 'ADM-H04'
      ? `/api/admin/m5/rescue-funnel?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [strategyName, setStrategyName] = useState('');
  const [couponRuleId, setCouponRuleId] = useState('');
  const [countdownMinutes, setCountdownMinutes] = useState(30);
  const [dailyUserLimit, setDailyUserLimit] = useState(1);
  const [orderLimit, setOrderLimit] = useState(1);
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState(() => {
    const value = new Date();
    value.setUTCFullYear(value.getUTCFullYear() + 1);
    return value.toISOString().slice(0, 10);
  });
  const [createBusy, setCreateBusy] = useState(false);
  const [createMessage, setCreateMessage] = useState('');
  const couponRules = rows(campaigns.data).flatMap((campaign) =>
    rows(campaign.rules).map((rule) => ({
      id: safe(rule.id),
      label: `${safe(campaign.name, '优惠活动')} · ${safe(rule.name, safe(rule.ruleType, '优惠规则'))}`,
    })),
  );
  const strategyRows = rows(strategies.data).filter((row) =>
    safe(row.name, '').toLowerCase().includes(search.toLowerCase()),
  );
  const funnelRows = rows(funnel.data);
  if (pageId === 'ADM-H04') {
    const max = Math.max(...funnelRows.map((row) => Number(row.count ?? 0)), 1);
    const total = funnelRows.reduce((sum, row) => sum + Number(row.count ?? 0), 0);
    const amount = funnelRows.reduce((sum, row) => sum + Number(row.amountCents ?? 0), 0);
    return (
      <>
        <Header title="支付挽回漏斗" subtitle="营销与推广 / 支付挽回 / 漏斗" />
        <Metrics
          items={[
            { label: '漏斗事件', value: total, note: '当前小程序范围' },
            { label: '漏斗阶段', value: funnelRows.length, note: '服务端聚合' },
            { label: '挽回金额', value: money(amount), note: '仅统计已有金额事实' },
            { label: '资格判断', value: '服务端', note: '与商户健康完全解耦' },
            { label: '数据隔离', value: '当前小程序', note: '不可跨 App 汇总' },
          ]}
        />
        <div className="r4-two weighted">
          <Panel title="支付挽回漏斗" hint="实时数据">
            <State state={funnel} count={funnelRows.length} noun="漏斗数据">
              <div className="r4-funnel">
                {funnelRows.map((row, index) => (
                  <div
                    key={`${safe(row.eventType)}-${index}`}
                    style={{ width: `${Math.max(48, (Number(row.count ?? 0) / max) * 100)}%` }}
                  >
                    <span>
                      {businessLabels[safe(row.eventType, '')] ?? safe(row.eventType, '业务事件')}
                    </span>
                    <b>{Number(row.count ?? 0).toLocaleString('zh-CN')}</b>
                  </div>
                ))}
              </div>
            </State>
          </Panel>
          <Panel title="阶段贡献排行">
            <State state={funnel} count={funnelRows.length} noun="阶段数据">
              <div className="r4-progress-list">
                {funnelRows.map((row) => (
                  <div key={safe(row.eventType)}>
                    <div>
                      <strong>{safe(row.eventType, '业务事件')}</strong>
                      <span>{Number(row.count ?? 0).toLocaleString('zh-CN')} 次</span>
                    </div>
                    <i>
                      <b style={{ width: `${(Number(row.count ?? 0) / max) * 100}%` }} />
                    </i>
                    <small>{money(row.amountCents)}</small>
                  </div>
                ))}
              </div>
            </State>
          </Panel>
        </div>
      </>
    );
  }
  async function toggle(row: JsonRecord) {
    await api.send(
      `/api/admin/m5/rescue-strategies/${encodeURIComponent(safe(row.id))}?appId=${encodeURIComponent(appId)}`,
      'PATCH',
      {
        enabled: row.enabled !== true,
        countdownMinutes: Number(row.countdownMinutes),
        dailyUserLimit: Number(row.dailyUserLimit),
        orderLimit: Number(row.orderLimit),
        validFrom: row.validFrom,
        validTo: row.validTo,
        expectedVersion: Number(row.version),
      },
    );
    strategies.reload();
  }
  async function createStrategy() {
    setCreateBusy(true);
    setCreateMessage('');
    try {
      await api.send(`/api/admin/m5/rescue-strategies?appId=${encodeURIComponent(appId)}`, 'POST', {
        name: strategyName.trim(),
        couponRuleId,
        countdownMinutes,
        dailyUserLimit,
        orderLimit,
        validFrom: new Date(`${validFrom}T00:00:00.000Z`).toISOString(),
        validTo: new Date(`${validTo}T23:59:59.999Z`).toISOString(),
        enabled: true,
      });
      setCreateMessage('挽回策略已创建，并已写入服务端审计记录。');
      setStrategyName('');
      setShowCreate(false);
      strategies.reload();
    } catch (error) {
      setCreateMessage(error instanceof Error ? error.message : '创建失败，请检查规则和权限。');
    } finally {
      setCreateBusy(false);
    }
  }
  return (
    <>
      <Header
        title="支付挽回策略"
        subtitle="营销与推广 / 支付挽回 / 策略"
        actions={
          <button
            className="primary"
            disabled={
              strategies.loading ||
              Boolean(strategies.error) ||
              campaigns.loading ||
              Boolean(campaigns.error)
            }
            onClick={() => setShowCreate((current) => !current)}
          >
            + 新建挽回策略
          </button>
        }
      />
      <Notice>
        支付挽回与商户健康完全独立；只有用户从微信收银台返回且服务端仍确认未支付时，才允许命中策略。
      </Notice>
      {showCreate && (
        <Panel title="新建支付挽回策略" hint="保存后由服务端按订单支付事实判断资格">
          <div className="r5-form-grid">
            <label>
              策略名称
              <input
                value={strategyName}
                onChange={(event) => setStrategyName(event.target.value)}
              />
            </label>
            <label>
              优惠规则
              <select
                value={couponRuleId}
                onChange={(event) => setCouponRuleId(event.target.value)}
              >
                <option value="">请选择当前小程序的有效优惠规则</option>
                {couponRules.map((rule) => (
                  <option key={rule.id} value={rule.id}>
                    {rule.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              展示倒计时（分钟）
              <input
                type="number"
                min="1"
                value={countdownMinutes}
                onChange={(event) => setCountdownMinutes(Number(event.target.value))}
              />
            </label>
            <label>
              单用户每日上限
              <input
                type="number"
                min="1"
                value={dailyUserLimit}
                onChange={(event) => setDailyUserLimit(Number(event.target.value))}
              />
            </label>
            <label>
              单订单上限
              <input
                type="number"
                min="1"
                value={orderLimit}
                onChange={(event) => setOrderLimit(Number(event.target.value))}
              />
            </label>
            <label>
              生效日期
              <input
                type="date"
                value={validFrom}
                onChange={(event) => setValidFrom(event.target.value)}
              />
            </label>
            <label>
              失效日期
              <input
                type="date"
                value={validTo}
                onChange={(event) => setValidTo(event.target.value)}
              />
            </label>
          </div>
          <div className="r5-form-actions">
            <button
              className="primary"
              disabled={
                createBusy ||
                !strategyName.trim() ||
                !couponRuleId ||
                !validFrom ||
                !validTo ||
                validTo < validFrom ||
                countdownMinutes < 1 ||
                dailyUserLimit < 1 ||
                orderLimit < 1
              }
              onClick={() => void createStrategy()}
            >
              {createBusy ? '创建中…' : '确认创建'}
            </button>
            <button disabled={createBusy} onClick={() => setShowCreate(false)}>
              取消
            </button>
          </div>
          {createMessage && <small className="r4-help">{createMessage}</small>}
        </Panel>
      )}
      <Panel title="策略列表" hint="高优先级 → 更精确范围 → 最近生效版本">
        <Filters search={search} setSearch={setSearch} placeholder="策略名称" />
        <State state={strategies} count={strategyRows.length} noun="挽回策略">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>策略名称</th>
                  <th>小程序范围</th>
                  <th>城市 / 产品</th>
                  <th>会员分群</th>
                  <th>优惠</th>
                  <th>倒计时</th>
                  <th>频控</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {strategyRows.map((row) => {
                  const rule = isRecord(row.couponRule) ? row.couponRule : {};
                  return (
                    <tr key={safe(row.id)}>
                      <td>
                        <b>{safe(row.name)}</b>
                        <small>{businessRef(row.id, '策略 ')}</small>
                      </td>
                      <td>当前小程序</td>
                      <td>
                        {row.cityId ? businessRef(row.cityId, '城市 ') : '全部城市'} /{' '}
                        {row.productId ? businessRef(row.productId, '产品 ') : '全部产品'}
                      </td>
                      <td>{safe(row.userSegment, '全部会员')}</td>
                      <td>
                        {money(rule.amountCents)} / 满 {money(rule.minSpendCents)}
                      </td>
                      <td>{Number(row.countdownMinutes)} 分钟</td>
                      <td>{Number(row.dailyUserLimit)} 次 / 日</td>
                      <td>
                        <Pill value={row.enabled === true ? 'ACTIVE' : 'PAUSED'} />
                      </td>
                      <td>
                        <button className="r5-link-button" onClick={() => void toggle(row)}>
                          {row.enabled === true ? '暂停' : '启用'}
                        </button>{' '}
                        <NavLink
                          href={pathFor('ADM-H02', {
                            id: isRecord(rule.campaign)
                              ? safe(rule.campaign.id, safe(row.id))
                              : safe(row.id),
                          })}
                        >
                          编辑
                        </NavLink>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
      <p className="operations-rule-note">
        同一订单只应用一条策略；已支付、已退款、风控拦截或已领取优惠的订单不重复发放。
      </p>
    </>
  );
}

function ChannelPages({
  api,
  appId,
  pageId,
  params,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
  params: Params;
}) {
  const base = `/api/admin/m6/apps/${encodeURIComponent(appId)}`;
  const channels = useApi(
    api,
    ['ADM-H05', 'ADM-H07', 'ADM-H08'].includes(pageId) ? `${base}/channels` : undefined,
  );
  const orgs = useApi(
    api,
    ['ADM-H05', 'ADM-H07', 'ADM-H08'].includes(pageId) ? `${base}/channel-orgs` : undefined,
  );
  const promoters = useApi(
    api,
    ['ADM-H07', 'ADM-H08'].includes(pageId) ? `${base}/promoters` : undefined,
  );
  const links = useApi(api, pageId === 'ADM-H08' ? `${base}/promotion-links` : undefined);
  const detail = useApi(
    api,
    pageId === 'ADM-H06' && params.id
      ? `${base}/channels/${encodeURIComponent(params.id)}`
      : undefined,
  );
  const [search, setSearch] = useState('');
  const [selectedLinkId, setSelectedLinkId] = useState('');
  const [showChannelForm, setShowChannelForm] = useState(false);
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [channelCode, setChannelCode] = useState('');
  const [channelName, setChannelName] = useState('');
  const [channelType, setChannelType] = useState('HOTEL');
  const [orgChannelId, setOrgChannelId] = useState('');
  const [orgParentId, setOrgParentId] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('HOTEL');
  const [settlementEntityName, setSettlementEntityName] = useState('');
  const [promoterChannelId, setPromoterChannelId] = useState('');
  const [promoterOrgId, setPromoterOrgId] = useState('');
  const [promoterCode, setPromoterCode] = useState('');
  const [promoterName, setPromoterName] = useState('');
  const [promoterContact, setPromoterContact] = useState('');
  const [showPromoterForm, setShowPromoterForm] = useState(false);
  const [linkPromoterId, setLinkPromoterId] = useState('');
  const [linkPageId, setLinkPageId] = useState('FE-CITY-005');
  const [linkExpiresAt, setLinkExpiresAt] = useState('');
  const createChannel = useCommand();
  const createOrg = useCommand();
  const channelStatus = useCommand();
  const createPromoter = useCommand();
  const promoterStatus = useCommand();
  const createLink = useCommand();
  const linkStatus = useCommand();
  const channelRows = rows(channels.data);
  const orgRows = rows(orgs.data);
  const promoterRows = rows(promoters.data);
  const linkRows = rows(links.data);
  const selectedPromoter = promoterRows.find((row) => safe(row.id, '') === linkPromoterId);
  const selectedPromoterOrgs = orgRows.filter(
    (row) => !promoterChannelId || safe(row.channelId, '') === promoterChannelId,
  );
  const selectedOrgParents = orgRows.filter(
    (row) => !orgChannelId || safe(row.channelId, '') === orgChannelId,
  );
  useEffect(() => {
    if (!linkRows.length) {
      setSelectedLinkId('');
      return;
    }
    if (!linkRows.some((row) => safe(row.id, '') === selectedLinkId)) {
      setSelectedLinkId(safe(linkRows[0]?.id, ''));
    }
  }, [linkRows, selectedLinkId]);
  if (pageId === 'ADM-H06') {
    const row = unwrapRecord(detail.data);
    const detailOrgs = Array.isArray(row.orgs) ? row.orgs.filter(isRecord) : [];
    const detailPromoters = Array.isArray(row.promoters) ? row.promoters.filter(isRecord) : [];
    const performance =
      Array.isArray(row.performance) && isRecord(row.performance[0]) ? row.performance[0] : {};
    const channelId = safe(row.id, params.id ?? '');
    const nextStatus = row.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    return (
      <>
        <Header
          title="渠道详情"
          subtitle="营销与推广 / 渠道分销 / 渠道详情"
          actions={
            <button
              className={row.status === 'ACTIVE' ? '' : 'primary'}
              disabled={channelStatus.busy || !channelId}
              onClick={() =>
                void channelStatus.run(
                  () =>
                    api.send(`${base}/channels/${encodeURIComponent(channelId)}/status`, 'POST', {
                      status: nextStatus,
                    }),
                  nextStatus === 'ACTIVE' ? '渠道已恢复' : '渠道已暂停',
                  detail.reload,
                )
              }
            >
              {channelStatus.busy ? '提交中…' : nextStatus === 'ACTIVE' ? '恢复渠道' : '暂停渠道'}
            </button>
          }
        />
        <CommandMessage command={channelStatus} />
        <State state={detail} count={Object.keys(row).length} noun="渠道详情">
          <div className="r4-three">
            <Panel title="渠道基本信息">
              <dl className="r4-kv">
                <div>
                  <dt>渠道名称</dt>
                  <dd>
                    <b>{safe(row.name)}</b>
                  </dd>
                </div>
                <div>
                  <dt>渠道编号</dt>
                  <dd>{safe(row.code)}</dd>
                </div>
                <div>
                  <dt>组织类型</dt>
                  <dd>{label(row.channelType, safe(row.channelType))}</dd>
                </div>
                <div>
                  <dt>状态</dt>
                  <dd>
                    <Pill value={row.status} />
                  </dd>
                </div>
              </dl>
            </Panel>
            <Panel title="本期经营数据">
              <dl className="r4-kv">
                <div>
                  <dt>归因订单</dt>
                  <dd>{Number(performance.attributedOrders ?? 0).toLocaleString('zh-CN')}</dd>
                </div>
                <div>
                  <dt>支付 GMV</dt>
                  <dd>{money(performance.attributedGmvCents)}</dd>
                </div>
                <div>
                  <dt>推广员</dt>
                  <dd>{detailPromoters.length}</dd>
                </div>
                <div>
                  <dt>组织节点</dt>
                  <dd>{detailOrgs.length}</dd>
                </div>
              </dl>
            </Panel>
            <Panel title="佣金摘要">
              <dl className="r4-kv">
                <div>
                  <dt>预估佣金</dt>
                  <dd>
                    <b>{money(performance.commissionCents)}</b>
                  </dd>
                </div>
                <div>
                  <dt>已确认</dt>
                  <dd>以佣金账本为准</dd>
                </div>
                <div>
                  <dt>退款冲销</dt>
                  <dd>以负向账本为准</dd>
                </div>
              </dl>
            </Panel>
          </div>
          <Panel title={`推广员 ${detailPromoters.length}`}>
            <div className="r5-table-scroll">
              <table className="r4-table">
                <thead>
                  <tr>
                    <th>推广员</th>
                    <th>专属编号</th>
                    <th>联系方式</th>
                    <th>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {detailPromoters.map((item) => (
                    <tr key={safe(item.id)}>
                      <td>{safe(item.displayName)}</td>
                      <td>{safe(item.promoterCode)}</td>
                      <td>{safe(item.contactMasked, '未授权查看')}</td>
                      <td>
                        <Pill value={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </State>
      </>
    );
  }
  if (pageId === 'ADM-H05') {
    const filteredChannels = channelRows.filter((row) =>
      safe(row.name, '').toLowerCase().includes(search.toLowerCase()),
    );
    return (
      <>
        <Header
          title="渠道组织树"
          subtitle="营销与推广 / 渠道分销 / 组织树"
          actions={
            <>
              <button onClick={() => setShowChannelForm((value) => !value)}>+ 新建渠道</button>
              <button
                className="primary"
                disabled={!channelRows.length}
                onClick={() => setShowOrgForm((value) => !value)}
              >
                + 新建组织
              </button>
            </>
          }
        />
        {showChannelForm && (
          <Panel title="新建渠道" hint="渠道创建后只属于当前小程序">
            <div className="r5-form-grid two">
              <label>
                渠道编号
                <input
                  value={channelCode}
                  maxLength={100}
                  placeholder="例如 HOTEL_PARTNER"
                  onChange={(event) => setChannelCode(event.target.value)}
                />
              </label>
              <label>
                渠道名称
                <input
                  value={channelName}
                  maxLength={200}
                  placeholder="填写运营可识别的名称"
                  onChange={(event) => setChannelName(event.target.value)}
                />
              </label>
              <label>
                渠道类型
                <select
                  value={channelType}
                  onChange={(event) => setChannelType(event.target.value)}
                >
                  <option value="HOTEL">酒店渠道</option>
                  <option value="AGENCY">旅行机构</option>
                  <option value="PROMOTER">推广合作</option>
                </select>
              </label>
            </div>
            <div className="r5-form-actions">
              <button onClick={() => setShowChannelForm(false)}>取消</button>
              <button
                className="primary"
                disabled={createChannel.busy || !validCode(channelCode) || !channelName.trim()}
                onClick={() =>
                  void createChannel.run(
                    () =>
                      api.send(`${base}/channels`, 'POST', {
                        code: channelCode.trim(),
                        name: channelName.trim(),
                        channelType,
                      }),
                    '渠道已创建',
                    () => {
                      setChannelCode('');
                      setChannelName('');
                      setShowChannelForm(false);
                      channels.reload();
                    },
                  )
                }
              >
                {createChannel.busy ? '创建中…' : '确认创建渠道'}
              </button>
            </div>
            <CommandMessage command={createChannel} />
          </Panel>
        )}
        {showOrgForm && (
          <Panel title="新建渠道组织" hint="可选择上级节点形成组织树">
            <div className="r5-form-grid two">
              <label>
                所属渠道
                <select
                  value={orgChannelId}
                  onChange={(event) => {
                    setOrgChannelId(event.target.value);
                    setOrgParentId('');
                  }}
                >
                  <option value="">请选择渠道</option>
                  {channelRows.map((row) => (
                    <option key={safe(row.id)} value={safe(row.id)}>
                      {businessName(row.name)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                上级组织（可选）
                <select
                  value={orgParentId}
                  onChange={(event) => setOrgParentId(event.target.value)}
                >
                  <option value="">作为一级组织</option>
                  {selectedOrgParents.map((row) => (
                    <option key={safe(row.id)} value={safe(row.id)}>
                      {businessName(row.name)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                组织编号
                <input
                  value={orgCode}
                  maxLength={100}
                  placeholder="例如 KUNMING_BRANCH"
                  onChange={(event) => setOrgCode(event.target.value)}
                />
              </label>
              <label>
                组织名称
                <input
                  value={orgName}
                  maxLength={200}
                  onChange={(event) => setOrgName(event.target.value)}
                />
              </label>
              <label>
                组织类型
                <select value={orgType} onChange={(event) => setOrgType(event.target.value)}>
                  <option value="HOTEL">酒店</option>
                  <option value="AGENCY">旅行机构</option>
                  <option value="PROMOTER">推广团队</option>
                </select>
              </label>
              <label>
                结算主体（可选）
                <input
                  value={settlementEntityName}
                  maxLength={300}
                  onChange={(event) => setSettlementEntityName(event.target.value)}
                />
              </label>
            </div>
            <div className="r5-form-actions">
              <button onClick={() => setShowOrgForm(false)}>取消</button>
              <button
                className="primary"
                disabled={createOrg.busy || !orgChannelId || !validCode(orgCode) || !orgName.trim()}
                onClick={() =>
                  void createOrg.run(
                    () =>
                      api.send(`${base}/channel-orgs`, 'POST', {
                        channelId: orgChannelId,
                        ...(orgParentId ? { parentId: orgParentId } : {}),
                        code: orgCode.trim(),
                        name: orgName.trim(),
                        orgType,
                        ...(settlementEntityName.trim()
                          ? { settlementEntityName: settlementEntityName.trim() }
                          : {}),
                      }),
                    '渠道组织已创建',
                    () => {
                      setOrgParentId('');
                      setOrgCode('');
                      setOrgName('');
                      setSettlementEntityName('');
                      setShowOrgForm(false);
                      orgs.reload();
                    },
                  )
                }
              >
                {createOrg.busy ? '创建中…' : '确认创建组织'}
              </button>
            </div>
            <CommandMessage command={createOrg} />
          </Panel>
        )}
        {!showChannelForm && <CommandMessage command={createChannel} />}
        {!showOrgForm && <CommandMessage command={createOrg} />}
        <div className="r5-channel-layout">
          <Panel title="渠道组织树">
            <State state={orgs} count={orgRows.length} noun="组织节点">
              <div className="r5-tree">
                {channelRows.map((channel) => (
                  <section key={safe(channel.id)}>
                    <strong>▾ {businessName(channel.name)}</strong>
                    {orgRows
                      .filter((org) => org.channelId === channel.id)
                      .map((org) => (
                        <div
                          key={safe(org.id)}
                          style={{
                            paddingLeft: `${Math.min(Number(org.depth ?? 0), 4) * 18 + 20}px`,
                          }}
                        >
                          ↳ {businessName(org.name)}{' '}
                          <small>{label(org.orgType, safe(org.orgType))}</small>
                        </div>
                      ))}
                  </section>
                ))}
              </div>
            </State>
          </Panel>
          <Panel title="渠道组织">
            <Filters search={search} setSearch={setSearch} placeholder="渠道组织名称" />
            <State state={channels} count={filteredChannels.length} noun="渠道">
              <div className="r5-table-scroll">
                <table className="r4-table">
                  <thead>
                    <tr>
                      <th>组织名称</th>
                      <th>类型</th>
                      <th>组织节点</th>
                      <th>状态</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChannels.map((row) => (
                      <tr key={safe(row.id)}>
                        <td>
                          <b>{businessName(row.name)}</b>
                          <small>{safe(row.code)}</small>
                        </td>
                        <td>{label(row.channelType, safe(row.channelType))}</td>
                        <td>{orgRows.filter((org) => org.channelId === row.id).length}</td>
                        <td>
                          <Pill value={row.status} />
                        </td>
                        <td>
                          <NavLink href={pathFor('ADM-H06', { id: safe(row.id) })}>详情</NavLink>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </State>
          </Panel>
        </div>
        <Notice>渠道与组织严格按当前小程序隔离；当前账号只能读取授权范围内的渠道。</Notice>
      </>
    );
  }
  if (pageId === 'ADM-H07') {
    const filtered = promoterRows.filter((row) =>
      `${safe(row.displayName, '')}${safe(row.promoterCode, '')}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
    async function toggle(row: JsonRecord) {
      await promoterStatus.run(
        () =>
          api.send(`${base}/promoters/${encodeURIComponent(safe(row.id))}/status`, 'POST', {
            status: row.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
          }),
        row.status === 'ACTIVE' ? '推广员已暂停' : '推广员已恢复',
        promoters.reload,
      );
    }
    return (
      <>
        <Header
          title="推广员管理"
          subtitle="营销与推广 / 渠道分销 / 推广员管理"
          actions={
            <button
              className="primary"
              disabled={!channelRows.length || !orgRows.length}
              onClick={() => setShowPromoterForm((value) => !value)}
            >
              + 新增推广员
            </button>
          }
        />
        {showPromoterForm && (
          <Panel title="新增推广员" hint="必须绑定当前小程序内的有效渠道和组织">
            <div className="r5-form-grid two">
              <label>
                所属渠道
                <select
                  value={promoterChannelId}
                  onChange={(event) => {
                    setPromoterChannelId(event.target.value);
                    setPromoterOrgId('');
                  }}
                >
                  <option value="">请选择渠道</option>
                  {channelRows
                    .filter((row) => row.status === 'ACTIVE')
                    .map((row) => (
                      <option key={safe(row.id)} value={safe(row.id)}>
                        {businessName(row.name)}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                所属组织
                <select
                  value={promoterOrgId}
                  onChange={(event) => setPromoterOrgId(event.target.value)}
                >
                  <option value="">请选择组织</option>
                  {selectedPromoterOrgs.map((row) => (
                    <option key={safe(row.id)} value={safe(row.id)}>
                      {businessName(row.name)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                推广员编号
                <input
                  value={promoterCode}
                  maxLength={100}
                  placeholder="例如 GUIDE_LI"
                  onChange={(event) => setPromoterCode(event.target.value)}
                />
              </label>
              <label>
                显示名称
                <input
                  value={promoterName}
                  maxLength={150}
                  onChange={(event) => setPromoterName(event.target.value)}
                />
              </label>
              <label>
                联系方式摘要（可选）
                <input
                  value={promoterContact}
                  maxLength={100}
                  placeholder="仅保存脱敏摘要"
                  onChange={(event) => setPromoterContact(event.target.value)}
                />
              </label>
            </div>
            <div className="r5-form-actions">
              <button onClick={() => setShowPromoterForm(false)}>取消</button>
              <button
                className="primary"
                disabled={
                  createPromoter.busy ||
                  !promoterChannelId ||
                  !promoterOrgId ||
                  !validCode(promoterCode) ||
                  !promoterName.trim()
                }
                onClick={() =>
                  void createPromoter.run(
                    () =>
                      api.send(`${base}/promoters`, 'POST', {
                        channelId: promoterChannelId,
                        channelOrgId: promoterOrgId,
                        promoterCode: promoterCode.trim(),
                        displayName: promoterName.trim(),
                        ...(promoterContact.trim()
                          ? { contactMasked: promoterContact.trim() }
                          : {}),
                      }),
                    '推广员已创建',
                    () => {
                      setPromoterCode('');
                      setPromoterName('');
                      setPromoterContact('');
                      setShowPromoterForm(false);
                      promoters.reload();
                    },
                  )
                }
              >
                {createPromoter.busy ? '创建中…' : '确认新增推广员'}
              </button>
            </div>
            <CommandMessage command={createPromoter} />
          </Panel>
        )}
        {!showPromoterForm && <CommandMessage command={createPromoter} />}
        <CommandMessage command={promoterStatus} />
        <Metrics
          items={[
            {
              label: '推广员总数',
              value: promoterRows.length,
              note: `正常 ${promoterRows.filter((row) => row.status === 'ACTIVE').length}`,
            },
            {
              label: '正常推广员',
              value: promoterRows.filter((row) => row.status === 'ACTIVE').length,
            },
            {
              label: '已停用',
              value: promoterRows.filter((row) => row.status === 'DISABLED').length,
            },
          ]}
        />
        <Panel title="推广员列表">
          <Filters search={search} setSearch={setSearch} placeholder="姓名 / 推广员编号" />
          <State state={promoters} count={filtered.length} noun="推广员">
            <div className="r5-table-scroll">
              <table className="r4-table">
                <thead>
                  <tr>
                    <th>推广员</th>
                    <th>所属渠道</th>
                    <th>推广员编号</th>
                    <th>联系方式</th>
                    <th>状态</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={safe(row.id)}>
                      <td>
                        <b>{safe(row.displayName)}</b>
                      </td>
                      <td>
                        {findName(channelRows, row.channelId, ['name']) ||
                          businessRef(row.channelId, '渠道 ')}
                      </td>
                      <td>{safe(row.promoterCode)}</td>
                      <td>{safe(row.contactMasked, '默认脱敏')}</td>
                      <td>
                        <Pill value={row.status} />
                      </td>
                      <td>
                        <button
                          className="r5-link-button"
                          disabled={promoterStatus.busy}
                          onClick={() => void toggle(row)}
                        >
                          {promoterStatus.busy
                            ? '提交中…'
                            : row.status === 'ACTIVE'
                              ? '暂停'
                              : '恢复'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </State>
        </Panel>
      </>
    );
  }
  const selected = linkRows.find((row) => safe(row.id, '') === selectedLinkId) ?? linkRows[0];
  return (
    <>
      <Header title="推广链接 / 二维码" subtitle="营销与推广 / 渠道分销 / 推广链接" />
      <div className="r4-two">
        <Panel title="推广入口">
          <label>
            推广员
            <select
              value={linkPromoterId}
              onChange={(event) => setLinkPromoterId(event.target.value)}
            >
              <option value="">请选择有效推广员</option>
              {promoterRows
                .filter((row) => row.status === 'ACTIVE')
                .map((row) => (
                  <option key={safe(row.id)} value={safe(row.id)}>
                    {safe(row.displayName, safe(row.promoterCode))}
                  </option>
                ))}
            </select>
          </label>
          <label>
            落地页面
            <select value={linkPageId} onChange={(event) => setLinkPageId(event.target.value)}>
              <option value="FE-CITY-005">城市选择页</option>
              <option value="FE-CITY-001">昆明线路页</option>
              <option value="FE-CITY-002">大理线路页</option>
              <option value="FE-CITY-003">丽江线路页</option>
              <option value="FE-CITY-004">香格里拉线路页</option>
              <option value="FE-DETAIL-001">昆明产品详情页</option>
              <option value="FE-DETAIL-002">大理产品详情页</option>
              <option value="FE-DETAIL-003">丽江产品详情页</option>
              <option value="FE-DETAIL-004">香格里拉产品详情页</option>
              <option value="FE-SEARCH-001">搜索结果页</option>
            </select>
          </label>
          <label>
            有效期（可选）
            <input
              type="datetime-local"
              value={linkExpiresAt}
              onChange={(event) => setLinkExpiresAt(event.target.value)}
            />
          </label>
          <button
            className="primary"
            disabled={
              createLink.busy ||
              !selectedPromoter ||
              !safe(selectedPromoter?.channelId, '') ||
              !safe(selectedPromoter?.channelOrgId, '') ||
              (Boolean(linkExpiresAt) && !isoDate(linkExpiresAt))
            }
            onClick={() =>
              void createLink.run(
                () =>
                  api.send(`${base}/promotion-links`, 'POST', {
                    channelId: safe(selectedPromoter?.channelId),
                    channelOrgId: safe(selectedPromoter?.channelOrgId),
                    promoterId: safe(selectedPromoter?.id),
                    targetPageId: linkPageId,
                    ...(linkExpiresAt ? { expiresAt: isoDate(linkExpiresAt) } : {}),
                  }),
                '推广链接已生成',
                () => {
                  setLinkExpiresAt('');
                  links.reload();
                },
              )
            }
          >
            {createLink.busy ? '生成中…' : '生成推广链接'}
          </button>
          <CommandMessage command={createLink} />
        </Panel>
        <Panel title="当前推广链接">
          <div className="operations-result-grid">
            <dl className="r4-kv">
              <div>
                <dt>场景码</dt>
                <dd>{selected ? safe(selected.code) : '暂未生成'}</dd>
              </div>
              <div>
                <dt>推广员</dt>
                <dd>
                  {findName(promoterRows, selected?.promoterId, ['displayName', 'promoterCode']) ||
                    '暂未记录'}
                </dd>
              </div>
              <div>
                <dt>落地页面</dt>
                <dd>{selected ? customerPageLabel(selected.targetPageId) : '暂未生成'}</dd>
              </div>
            </dl>
            <State state={links} count={selected ? 1 : 0} noun="推广链接">
              <PromotionCodePreview
                api={api}
                appId={appId}
                {...(selected ? { link: selected } : {})}
              />
            </State>
          </div>
          <p className="operations-rule-note">
            下方选择链接可查看对应二维码。订单创建后归因固定，推广员变更不影响历史订单。
          </p>
        </Panel>
      </div>
      <Panel title="推广链接列表">
        <State state={links} count={linkRows.length} noun="推广链接">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>链接编码</th>
                  <th>推广员</th>
                  <th>落地页面</th>
                  <th>目标产品</th>
                  <th>有效期</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {linkRows.map((row) => (
                  <tr key={safe(row.id)}>
                    <td>{businessRef(row.id, '推广链接 ')}</td>
                    <td>
                      {findName(promoterRows, row.promoterId, ['displayName', 'promoterCode']) ||
                        businessRef(row.promoterId, '推广员 ')}
                    </td>
                    <td>{customerPageLabel(row.targetPageId)}</td>
                    <td>
                      {row.targetProductId ? businessRef(row.targetProductId, '产品 ') : '未限定'}
                    </td>
                    <td>{row.expiresAt ? date(row.expiresAt) : '长期有效'}</td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                    <td>
                      <button
                        className="r5-link-button"
                        onClick={() => setSelectedLinkId(safe(row.id, ''))}
                      >
                        查看二维码
                      </button>
                      <button
                        className="r5-link-button"
                        disabled={linkStatus.busy}
                        onClick={() =>
                          void linkStatus.run(
                            () =>
                              api.send(
                                `${base}/promotion-links/${encodeURIComponent(safe(row.id))}/status`,
                                'POST',
                                { status: row.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' },
                              ),
                            row.status === 'ACTIVE' ? '推广链接已停用' : '推广链接已恢复',
                            links.reload,
                          )
                        }
                      >
                        {linkStatus.busy ? '提交中…' : row.status === 'ACTIVE' ? '停用' : '恢复'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
      <CommandMessage command={linkStatus} />
    </>
  );
}

function CommissionPages({
  api,
  appId,
  pageId,
}: {
  api: AdminApiClient;
  appId: string;
  pageId: string;
}) {
  const base = `/api/admin/m6/apps/${encodeURIComponent(appId)}`;
  const rules = useApi(api, pageId === 'ADM-H09' ? `${base}/commission-rules` : undefined);
  const ledger = useApi(api, pageId === 'ADM-H10' ? `${base}/commission-ledger` : undefined);
  const batches = useApi(api, pageId === 'ADM-H10' ? `${base}/settlement-batches` : undefined);
  const channels = useApi(
    api,
    ['ADM-H09', 'ADM-H10'].includes(pageId) ? `${base}/channels` : undefined,
  );
  const orgs = useApi(
    api,
    ['ADM-H09', 'ADM-H10'].includes(pageId) ? `${base}/channel-orgs` : undefined,
  );
  const promoters = useApi(api, pageId === 'ADM-H09' ? `${base}/promoters` : undefined);
  const products = useApi(
    api,
    pageId === 'ADM-H09' ? `/api/admin/m2/products?appId=${encodeURIComponent(appId)}` : undefined,
  );
  const ruleRows = rows(rules.data);
  const channelRows = rows(channels.data);
  const orgRows = rows(orgs.data);
  const promoterRows = rows(promoters.data);
  const productRows = rows(products.data);
  const skuRows = productRows.flatMap((product) =>
    Array.isArray(product.skus)
      ? product.skus
          .filter(isRecord)
          .map((sku): JsonRecord => ({ ...sku, productTitle: product.title }))
      : [],
  );
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleCode, setRuleCode] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [ruleScopeType, setRuleScopeType] = useState('ALL');
  const [ruleScopeId, setRuleScopeId] = useState('');
  const [calculationMode, setCalculationMode] = useState('PERCENTAGE');
  const [commissionValue, setCommissionValue] = useState('');
  const [settlementCycle, setSettlementCycle] = useState('MONTHLY');
  const [ruleValidFrom, setRuleValidFrom] = useState('');
  const [ruleValidTo, setRuleValidTo] = useState('');
  const [refundReversalEnabled, setRefundReversalEnabled] = useState(true);
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [showSettlementForm, setShowSettlementForm] = useState(false);
  const [settlementChannelId, setSettlementChannelId] = useState('');
  const [settlementOrgId, setSettlementOrgId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [settlementKey, setSettlementKey] = useState(() => operationKey('settlement'));
  const createRule = useCommand();
  const createSettlement = useCommand();
  const settlementTransition = useCommand();
  const scopeRows =
    ruleScopeType === 'CHANNEL'
      ? channelRows
      : ruleScopeType === 'CHANNEL_ORG'
        ? orgRows
        : ruleScopeType === 'PROMOTER'
          ? promoterRows
          : ruleScopeType === 'PRODUCT'
            ? productRows
            : ruleScopeType === 'SKU'
              ? skuRows
              : [];
  const settlementOrgRows = orgRows.filter(
    (row) => !settlementChannelId || safe(row.channelId, '') === settlementChannelId,
  );
  if (pageId === 'ADM-H09') {
    return (
      <>
        <Header
          title="佣金规则"
          subtitle="营销与推广 / 渠道分销 / 佣金规则"
          actions={
            <button className="primary" onClick={() => setShowRuleForm((value) => !value)}>
              + 新建佣金规则
            </button>
          }
        />
        <Notice warning>
          佣金规则必须版本化。订单创建后保存归因与规则快照；退款按原规则生成负向冲销流水。
        </Notice>
        {showRuleForm && (
          <Panel title="新建佣金规则版本" hint="相同规则编号会形成新的受控版本">
            <div className="r5-form-grid two">
              <label>
                规则编号
                <input
                  value={ruleCode}
                  maxLength={100}
                  placeholder="例如 HOTEL_DEFAULT"
                  onChange={(event) => setRuleCode(event.target.value)}
                />
              </label>
              <label>
                规则名称
                <input
                  value={ruleName}
                  maxLength={200}
                  onChange={(event) => setRuleName(event.target.value)}
                />
              </label>
              <label>
                适用范围
                <select
                  value={ruleScopeType}
                  onChange={(event) => {
                    setRuleScopeType(event.target.value);
                    setRuleScopeId('');
                  }}
                >
                  <option value="ALL">当前小程序全部业务</option>
                  <option value="CHANNEL">指定渠道</option>
                  <option value="CHANNEL_ORG">指定渠道组织</option>
                  <option value="PROMOTER">指定推广员</option>
                  <option value="PRODUCT">指定产品</option>
                  <option value="SKU">指定服务套餐</option>
                </select>
              </label>
              {ruleScopeType !== 'ALL' && (
                <label>
                  适用对象
                  <select
                    value={ruleScopeId}
                    onChange={(event) => setRuleScopeId(event.target.value)}
                  >
                    <option value="">请选择业务对象</option>
                    {scopeRows.map((row) => (
                      <option key={safe(row.id)} value={safe(row.id)}>
                        {safe(
                          row.name ?? row.displayName ?? row.title ?? row.skuName,
                          businessRef(row.id, '业务对象 '),
                        )}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                计算方式
                <select
                  value={calculationMode}
                  onChange={(event) => {
                    setCalculationMode(event.target.value);
                    setCommissionValue('');
                  }}
                >
                  <option value="PERCENTAGE">销售额百分比</option>
                  <option value="FIXED">每单固定金额</option>
                </select>
              </label>
              <label>
                {calculationMode === 'FIXED' ? '固定佣金（元）' : '佣金比例（%）'}
                <input
                  type="number"
                  min="0"
                  max={calculationMode === 'PERCENTAGE' ? '100' : undefined}
                  step="0.01"
                  value={commissionValue}
                  onChange={(event) => setCommissionValue(event.target.value)}
                />
              </label>
              <label>
                结算周期
                <select
                  value={settlementCycle}
                  onChange={(event) => setSettlementCycle(event.target.value)}
                >
                  <option value="MONTHLY">按月结算</option>
                  <option value="WEEKLY">按周结算</option>
                  <option value="MANUAL">人工周期</option>
                </select>
              </label>
              <label>
                生效日期
                <input
                  type="date"
                  value={ruleValidFrom}
                  onChange={(event) => setRuleValidFrom(event.target.value)}
                />
              </label>
              <label>
                失效日期（可选）
                <input
                  type="date"
                  min={ruleValidFrom || undefined}
                  value={ruleValidTo}
                  onChange={(event) => setRuleValidTo(event.target.value)}
                />
              </label>
              <label className="r5-checkbox-label">
                <input
                  type="checkbox"
                  checked={refundReversalEnabled}
                  onChange={(event) => setRefundReversalEnabled(event.target.checked)}
                />
                退款时按原规则冲销
              </label>
              <label className="r5-checkbox-label">
                <input
                  type="checkbox"
                  checked={ruleEnabled}
                  onChange={(event) => setRuleEnabled(event.target.checked)}
                />
                创建后立即启用
              </label>
            </div>
            <div className="r5-form-actions">
              <button onClick={() => setShowRuleForm(false)}>取消</button>
              <button
                className="primary"
                disabled={
                  createRule.busy ||
                  !validCode(ruleCode) ||
                  !ruleName.trim() ||
                  (ruleScopeType !== 'ALL' && !ruleScopeId) ||
                  !ruleValidFrom ||
                  (calculationMode === 'FIXED'
                    ? !Number.isSafeInteger(parseYuanCents(commissionValue))
                    : !/^\d+(?:\.\d{1,2})?$/u.test(commissionValue) ||
                      Number(commissionValue) > 100) ||
                  (Boolean(ruleValidTo) && ruleValidTo < ruleValidFrom)
                }
                onClick={() => {
                  const percentageBps = Math.round(Number(commissionValue) * 100);
                  void createRule.run(
                    () =>
                      api.send(`${base}/commission-rules`, 'POST', {
                        ruleCode: ruleCode.trim(),
                        name: ruleName.trim(),
                        scopeType: ruleScopeType,
                        ...(ruleScopeType === 'ALL' ? {} : { scopeId: ruleScopeId }),
                        calculationMode,
                        ...(calculationMode === 'FIXED'
                          ? { fixedAmountCents: parseYuanCents(commissionValue) }
                          : { rateBps: percentageBps }),
                        refundReversalEnabled,
                        settlementCycle,
                        eligibleOrderStatuses: ['COMPLETED'],
                        validFrom: new Date(`${ruleValidFrom}T00:00:00+08:00`).toISOString(),
                        ...(ruleValidTo
                          ? { validTo: new Date(`${ruleValidTo}T23:59:59+08:00`).toISOString() }
                          : {}),
                        enabled: ruleEnabled,
                      }),
                    '佣金规则版本已创建',
                    () => {
                      setRuleName('');
                      setCommissionValue('');
                      setShowRuleForm(false);
                      rules.reload();
                    },
                  );
                }}
              >
                {createRule.busy ? '创建中…' : '创建规则版本'}
              </button>
            </div>
            <CommandMessage command={createRule} />
          </Panel>
        )}
        {!showRuleForm && <CommandMessage command={createRule} />}
        <Panel title="规则列表">
          <State state={rules} count={ruleRows.length} noun="佣金规则">
            <div className="r5-table-scroll">
              <table className="r4-table">
                <thead>
                  <tr>
                    <th>规则名称</th>
                    <th>适用对象</th>
                    <th>适用范围</th>
                    <th>计算方式</th>
                    <th>佣金值</th>
                    <th>退款处理</th>
                    <th>结算周期</th>
                    <th>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {ruleRows.map((row) => (
                    <tr key={safe(row.id)}>
                      <td>
                        <b>{safe(row.name, safe(row.ruleCode))}</b>
                        <small>
                          {safe(row.ruleCode)} · 版本 {Number(row.versionNumber ?? 1)}
                        </small>
                      </td>
                      <td>{label(row.scopeType, safe(row.scopeType))}</td>
                      <td>
                        {row.scopeId ? businessRef(row.scopeId, '业务对象 ') : '当前小程序全部业务'}
                      </td>
                      <td>{label(row.calculationMode, safe(row.calculationMode))}</td>
                      <td>
                        {row.calculationMode === 'FIXED'
                          ? money(row.fixedAmountCents)
                          : percentFromBps(row.rateBps)}
                      </td>
                      <td>{row.refundReversalEnabled === false ? '不冲销' : '退款冲销'}</td>
                      <td>{safe(row.settlementCycle, '按配置周期')}</td>
                      <td>
                        <Pill value={row.enabled === true ? 'ACTIVE' : 'DISABLED'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </State>
        </Panel>
        <p className="operations-rule-note">
          订单完成后确认佣金，取消订单不计佣；规则变更不重算历史订单。
        </p>
      </>
    );
  }
  const ledgerRows = rows(ledger.data);
  const batchRows = rows(batches.data);
  const payable = batchRows.reduce((sum, row) => sum + Number(row.payableAmountCents ?? 0), 0);
  const reversals = ledgerRows
    .filter((row) => row.entryType === 'REVERSAL')
    .reduce((sum, row) => sum + Number(row.amountCents ?? 0), 0);
  async function transition(row: JsonRecord, action: 'submit' | 'approve') {
    await settlementTransition.run(
      () =>
        api.send(
          `${base}/settlement-batches/${encodeURIComponent(safe(row.id))}/${action}`,
          'POST',
          { expectedVersion: Number(row.version) },
        ),
      action === 'submit' ? '结算批次已提交审核' : '结算批次已复核通过',
      batches.reload,
    );
  }
  return (
    <>
      <Header
        title="佣金结算批次"
        subtitle="营销与推广 / 渠道分销 / 佣金结算"
        actions={
          <button
            className="primary"
            disabled={!channelRows.length}
            onClick={() => setShowSettlementForm((value) => !value)}
          >
            + 生成结算批次
          </button>
        }
      />
      {showSettlementForm && (
        <Panel title="生成结算批次" hint="只汇总所选周期内已确认且尚未进入结算的佣金流水">
          <div className="r5-form-grid two">
            <label>
              结算渠道
              <select
                value={settlementChannelId}
                onChange={(event) => {
                  setSettlementChannelId(event.target.value);
                  setSettlementOrgId('');
                }}
              >
                <option value="">请选择渠道</option>
                {channelRows
                  .filter((row) => row.status === 'ACTIVE')
                  .map((row) => (
                    <option key={safe(row.id)} value={safe(row.id)}>
                      {businessName(row.name)}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              结算组织（可选）
              <select
                value={settlementOrgId}
                onChange={(event) => setSettlementOrgId(event.target.value)}
              >
                <option value="">渠道全部组织</option>
                {settlementOrgRows.map((row) => (
                  <option key={safe(row.id)} value={safe(row.id)}>
                    {businessName(row.name)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              周期开始
              <input
                type="date"
                value={periodStart}
                onChange={(event) => setPeriodStart(event.target.value)}
              />
            </label>
            <label>
              周期结束
              <input
                type="date"
                min={periodStart || undefined}
                value={periodEnd}
                onChange={(event) => setPeriodEnd(event.target.value)}
              />
            </label>
          </div>
          <Notice warning>生成与复核必须由不同职责人员完成；本页面不会触发自动打款。</Notice>
          <div className="r5-form-actions">
            <button onClick={() => setShowSettlementForm(false)}>取消</button>
            <button
              className="primary"
              disabled={
                createSettlement.busy ||
                !settlementChannelId ||
                !periodStart ||
                !periodEnd ||
                periodEnd < periodStart
              }
              onClick={() =>
                void createSettlement.run(
                  () =>
                    api.request(`${base}/settlement-batches`, {
                      method: 'POST',
                      headers: { 'idempotency-key': settlementKey },
                      body: JSON.stringify({
                        channelId: settlementChannelId,
                        ...(settlementOrgId ? { channelOrgId: settlementOrgId } : {}),
                        periodStart: new Date(`${periodStart}T00:00:00+08:00`).toISOString(),
                        periodEnd: new Date(`${periodEnd}T23:59:59+08:00`).toISOString(),
                      }),
                    }),
                  '结算批次已生成',
                  () => {
                    setPeriodStart('');
                    setPeriodEnd('');
                    setSettlementKey(operationKey('settlement'));
                    setShowSettlementForm(false);
                    batches.reload();
                    ledger.reload();
                  },
                )
              }
            >
              {createSettlement.busy ? '生成中…' : '确认生成结算批次'}
            </button>
          </div>
          <CommandMessage command={createSettlement} />
        </Panel>
      )}
      {!showSettlementForm && <CommandMessage command={createSettlement} />}
      <CommandMessage command={settlementTransition} />
      <Metrics
        items={[
          { label: '应结金额', value: money(payable), note: `${batchRows.length} 个批次` },
          {
            label: '退款冲销',
            value: money(reversals),
            note: `${ledgerRows.filter((row) => row.entryType === 'REVERSAL').length} 笔`,
          },
          {
            label: '待审核',
            value: batchRows.filter((row) => row.status === 'REVIEW_PENDING').length,
            note: '需独立复核',
          },
          {
            label: '已结算',
            value: batchRows.filter((row) => row.status === 'SETTLED').length,
            note: '不可反向修改',
          },
          { label: '异常待复核', value: 0, note: '按当前接口可见事实' },
        ]}
      />
      <Panel title="结算批次">
        <State state={batches} count={batchRows.length} noun="结算批次">
          <div className="r5-table-scroll">
            <table className="r4-table">
              <thead>
                <tr>
                  <th>批次号</th>
                  <th>结算周期</th>
                  <th>结算渠道</th>
                  <th>应计佣金</th>
                  <th>退款冲销</th>
                  <th>应结金额</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {batchRows.map((row) => (
                  <tr key={safe(row.id)}>
                    <td>
                      <b>{safe(row.batchNo)}</b>
                    </td>
                    <td>
                      {date(row.periodStart)} 至 {date(row.periodEnd)}
                    </td>
                    <td>{businessRef(row.channelId, '渠道 ')}</td>
                    <td>{money(row.grossCommissionCents)}</td>
                    <td>{money(row.reversalAmountCents)}</td>
                    <td>
                      <b>{money(row.payableAmountCents)}</b>
                    </td>
                    <td>
                      <Pill value={row.status} />
                    </td>
                    <td>
                      {row.status === 'DRAFT' ? (
                        <button
                          className="r5-link-button"
                          disabled={settlementTransition.busy}
                          onClick={() => void transition(row, 'submit')}
                        >
                          提交审核
                        </button>
                      ) : row.status === 'REVIEW_PENDING' ? (
                        <button
                          className="r5-link-button"
                          disabled={settlementTransition.busy}
                          onClick={() => void transition(row, 'approve')}
                        >
                          复核通过
                        </button>
                      ) : (
                        <span>查看</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
      <p className="operations-rule-note">
        结算须独立财务复核，生成与复核人员分离；实际打款需授权与外部凭据，退款以负向流水冲销。
      </p>
    </>
  );
}

function MarketingChannelContent({ api, targetAppId, pageId, params }: PageProps) {
  if (!targetAppId) return <EmptyApp />;
  if (pageId === 'ADM-H01' || pageId === 'ADM-H02')
    return <CouponPages api={api} appId={targetAppId} pageId={pageId} params={params} />;
  if (pageId === 'ADM-H03' || pageId === 'ADM-H04')
    return <RescuePages api={api} appId={targetAppId} pageId={pageId} />;
  if (['ADM-H05', 'ADM-H06', 'ADM-H07', 'ADM-H08'].includes(pageId))
    return <ChannelPages api={api} appId={targetAppId} pageId={pageId} params={params} />;
  return <CommissionPages api={api} appId={targetAppId} pageId={pageId} />;
}

export function CanonicalMarketingChannelPage(props: PageProps) {
  return (
    <div className="operations-workspace">
      <MarketingChannelContent {...props} />
    </div>
  );
}
